'use client';

import { Add } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { campaignManagerTheme } from '@campaign-manager/theme';

import Button from '../Button';

interface CtaConfig {
  legend?: string;
  button?: ReactNode;
  buttonLabel?: string;
  buttonHref?: string;
  buttonOnClick?: () => void;
}
interface Props {
  title?: string;
  cta?: CtaConfig;
  resetAllFilters?: () => void;
}

export default function TableEmptyState({ title, cta, resetAllFilters }: Props) {
  const isFilterTooNarrow = !!resetAllFilters;

  if (isFilterTooNarrow) {
    return (
      <Stack
        sx={{
          gap: 1,
          alignItems: 'center',
          py: 4,
        }}
      >
        <RestrictiveFilterEmptyState resetAllFilters={resetAllFilters} />
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        gap: 1,
        alignItems: 'center',
        py: 4,
      }}
    >
      <Title title={title} />
      <Cta cta={cta} />
    </Stack>
  );
}

interface RestrictiveFilterEmptyStateProps {
  resetAllFilters: () => void;
}

function RestrictiveFilterEmptyState({ resetAllFilters }: RestrictiveFilterEmptyStateProps) {
  return (
    <>
      <Title title="No results" />
      <Typography>
        Filters may be too restrictive.{' '}
        <a
          href="#"
          onClick={() => resetAllFilters()}
          style={{
            textDecoration: 'none',
            color: campaignManagerTheme.color.primary,
          }}
        >
          Clear filters
        </a>
      </Typography>
    </>
  );
}

interface TitleProps {
  title?: string;
}

function Title({ title = 'No data' }: TitleProps) {
  return (
    <Typography
      variant="h6"
      component="h3"
    >
      {title}
    </Typography>
  );
}

interface CtaProps {
  cta?: CtaConfig;
}

function Cta({ cta }: CtaProps) {
  if (!cta) {
    return null;
  }

  const { legend, button, buttonLabel, buttonHref, buttonOnClick } = cta;

  if (button) {
    return (
      <>
        <Typography>{legend}</Typography>
        {button}
      </>
    );
  }

  return (
    <>
      <Typography>{legend}</Typography>
      {buttonLabel && (
        <Button
          type="submit"
          variant="outlined"
          startIcon={<Add />}
          sx={{ mt: 1 }}
          {...(buttonHref ? { href: buttonHref } : {})}
          {...(buttonOnClick ? { onClick: buttonOnClick } : {})}
        >
          {buttonLabel}
        </Button>
      )}
    </>
  );
}
